import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from "react-router-dom";
import './style/editor.scss';
import { parseMarkup } from 'openlexia-markup';

import Button from '../../shared/components/Button';
import Textarea from '../../shared/components/Textarea';
import Input from "../../shared/components/Input";
import UploadZone from "../../shared/components/UploadZone";
import MediaList from "../../shared/components/MediaList";
import { saveDraft } from './infra/repository/SaveDraft';
import { publishDraft} from "./infra/repository/PublishDraft";
import { findDraftById} from "./infra/repository/FindDraftById";
import { uploadMedia } from './infra/repository/UploadMedia';
import { deleteMedia } from './infra/repository/DeleteMedia';
import { findPlaygroundDraft } from './infra/repository/FindPlaygroundDraft';

const PREVIEW_DELAY_MS = 1000;



export default function DraftEditor({ mode = 'edit' }) {
    const isPlayground = mode === 'playground';
    const navigate = useNavigate();
    const busy = useRef(false);
    const [courseId, setCourseId] = useState(null);
    const [parentPageId, setParentPageId] = useState(null);
    const [conflict, setConflict] = useState(false);
    const [operationError, setOperationError] = useState('');
    const [status, setStatus] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    // get url params
    const location = useLocation();
    const requestedDraftId = new URLSearchParams(location.search).get('draftId');

    const [pageName, setPageName] = useState('');
    const [markup, setMarkup] = useState('');
    const [draftId, setDraftId] = useState(requestedDraftId);
    const [htmlPreview, setHtmlPreview] = useState('');
    const [loadError, setLoadError] = useState('');
    const [medias, setMedias] = useState([]);

    const reloadMediaList = async () => {
        const data = await findDraftById(draftId);
        setMedias(data.medias);
    };

    const onDropMedia = async (media) => {
        if (isPlayground) return;
        try {
            await uploadMedia(draftId, media.name, media.type, media.file);
            console.log('Upload successful:');
            await reloadMediaList();
        } catch (err) {
            console.log(err.message);
        }
    };

    const onDeleteMedia = async (mediaId) => {
        if (isPlayground) return;
        try {
            await deleteMedia(draftId, mediaId);
            console.log('Media deleted successfully:');
            await reloadMediaList()
        } catch (error) {
            console.error('Error deleting media:', error);
            throw error;
        }
    };

    const currentDraft = () => ({ id: draftId, name: pageName, markup });

    const save = async () => {
        if (isPlayground || busy.current) return;
        busy.current = true; setOperationError(''); setStatus('Saving…');
        try { await saveDraft(currentDraft()); setStatus('Draft saved.'); }
        catch (error) { setOperationError(error.response?.data?.error || 'Unable to save. Your changes are still available.'); setStatus(''); }
        finally { busy.current = false; }
    };

    const publish = async () => {
        if (isPlayground || busy.current) return;
        busy.current = true; setOperationError(''); setStatus('Publishing…');
        try {
            // Save the latest fields and format the current source, not a debounced preview.
            const html = parseMarkup(markup) || '';
            await saveDraft(currentDraft());
            const pageId = await publishDraft(draftId, html);
            if (!pageId) throw new Error('No published page ID returned');
            await navigate(`/lecture?courseId=${encodeURIComponent(courseId)}&pageId=${encodeURIComponent(pageId)}`);
        } catch (error) {
            setOperationError(error.response?.data?.error || 'Unable to publish. Your draft and changes are still available to retry.');
            setStatus('');
            setConflict(error.response?.status === 409);
        } finally { busy.current = false; }
    };

    useEffect(() => {
        let active = true;
        setLoadError('');
        setIsLoading(true);
        setDraftId(null);
        setCourseId(null);
        setOperationError('');
        setStatus('');
        setConflict(false);
        setPageName('');
        setMarkup('');
        setMedias([]);
        const request = isPlayground ? findPlaygroundDraft() : findDraftById(requestedDraftId);
        request.then(data => {
            if (!active) return;
            setPageName(data.name ?? '');
            setMarkup(data.markup ?? '');
            setDraftId(data.id);
            setCourseId(data.courseId);
            setParentPageId(data.parentPageId);
            setMedias(data.medias ?? []);
            setIsLoading(false);
        }).catch(() => {
            if (active) { setLoadError('Unable to load the draft. Please try again.'); setIsLoading(false); }
        });
        return () => { active = false; };
    }, [isPlayground, requestedDraftId]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            setHtmlPreview(parseMarkup(markup) || '');
        }, PREVIEW_DELAY_MS);
        return () => clearTimeout(timeout);
    }, [markup]);

    return (
        <main className="draft-editor">
            <header className="draft-editor__header">
                <div>
                    <p className="draft-editor__eyebrow">Creator workspace</p>
                    <h1>{isPlayground ? 'Editor Playground' : 'Draft editor'}</h1>
                    <p className="draft-editor__description">Shape your lesson and see how it reads, side by side.</p>
                </div>
                {!isPlayground && <div className="draft-editor__actions">
                    <Button className="draft-editor__save" text="Save draft" onClick={save} disabled={isLoading || !draftId || Boolean(loadError) || status === 'Saving…' || status === 'Publishing…'} />
                    <Button className="draft-editor__publish" text="Publish" onClick={publish} disabled={isLoading || !draftId || Boolean(loadError) || status === 'Saving…' || status === 'Publishing…'} />
                </div>}
            </header>

            {isPlayground && <p className="draft-editor__notice">Your edits stay in this page. Refresh to start again. Saving, publishing, and media changes are unavailable.</p>}
            {conflict && parentPageId && <Link to={`/editor/page/${parentPageId}/edit?fresh=true`}>Start a fresh editing draft (keeps this draft)</Link>}
            {operationError && <p className="draft-editor__error" role="alert">{operationError}</p>}
            {!isPlayground && <p><output>{isLoading ? 'Loading draft…' : status}</output></p>}
            {loadError && <p className="draft-editor__error" role="alert">{loadError}</p>}

            <div className="draft-editor__settings">
                <div className="draft-editor__name">
                    <label htmlFor="draft-page-name">Page name</label>
                    <Input
                        disabled={isLoading || status === 'Publishing…' || status === 'Saving…'}
                        idInput="draft-page-name"
                        classList="text"
                        placeholder="Lecture name"
                        value={pageName}
                        onChange={(e) => setPageName(e.target.value)}
                    />
                </div>
                {!isPlayground && !isLoading && <details className="draft-editor__media">
                    <summary>Lesson media <span>{medias.length} files</span></summary>
                    <div className="draft-editor__media-content">
                        <p>Upload media, then copy its ID to use in your lesson.</p>
                        <UploadZone draftId={draftId} onDropMedia={onDropMedia} />
                        <MediaList medias={medias} onDeleteMedia={onDeleteMedia} />
                    </div>
                </details>}
            </div>

            {/* Keyboard users need focus here to scroll the workspace and preview. */}
            <section className="draft-editor__workspace" aria-label="Lesson editing workspace" tabIndex={0}>
                <div className="draft-editor__panes">
                    <section className="draft-editor__source" aria-label="Markup editor">
                        <div className="draft-editor__panel-header">
                            <h2 id="draft-markup-heading"><label htmlFor="draft-markup">Markup</label></h2>
                            <span>Lesson source</span>
                        </div>
                        <Textarea
                            disabled={isLoading || status === 'Publishing…' || status === 'Saving…'}
                            idTextarea="draft-markup"
                            placeholder="Write your markup here..."
                            spellCheck={false}
                            value={markup}
                            onChange={(e) => setMarkup(e.target.value)}
                        />
                    </section>

                    <section className="draft-editor__preview" aria-labelledby="draft-preview-heading">
                        <div className="draft-editor__panel-header">
                            <h2 id="draft-preview-heading">Preview</h2>
                            <span>Updates as you type</span>
                        </div>
                        <section className="draft-editor__preview-content" tabIndex={0}
                            aria-label="Lesson preview"
                            dangerouslySetInnerHTML={{ __html: htmlPreview }}
                        />
                    </section>
                </div>
            </section>
            <p className="draft-editor__hint">Explore the <Link to="/editor/doc/editor/markup">markup guide</Link> for formatting, audio, tables, and writing exercises.</p>
        </main>
    );
};
